'use client';
import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface MembershipOffer {
  route: string;
  name: string;
  tagline: string | null;
  audience: string | null;
  foundingPricePence: number | null;
  standardPricePence: number | null;
  foundingPlaces: number | null;
  foundingPlacesLeft: number | null;
  currentPricePence: number | null;
  billing: string;
  features: string[];
  notIncluded: string | null;
  paymentUrl: string | null;
  sortOrder: number;
}

export const PLAN_RANK: Record<string, number> = { starter: 1, growth: 2, growth_governance: 3 };

function toNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function mapOffer(r: any): MembershipOffer {
  return {
    route: String(r?.route ?? ''),
    name: String(r?.name ?? ''),
    tagline: r?.tagline ?? null,
    audience: r?.audience ?? null,
    foundingPricePence: toNum(r?.founding_price_pence),
    standardPricePence: toNum(r?.standard_price_pence),
    foundingPlaces: toNum(r?.founding_places),
    foundingPlacesLeft: toNum(r?.founding_places_left),
    currentPricePence: toNum(r?.current_price_pence),
    billing: String(r?.billing ?? ''),
    features: Array.isArray(r?.features) ? r.features.map((f: unknown) => String(f)) : [],
    notIncluded: r?.not_included ?? null,
    paymentUrl: r?.payment_url ?? null,
    sortOrder: toNum(r?.sort_order) ?? 0,
  };
}

export async function fetchMembershipOffers(): Promise<MembershipOffer[]> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('membership_offers_public');
  if (error) throw new Error(error.message);
  const rows = Array.isArray(data) ? data : data ? [data] : [];
  return rows.map(mapOffer).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function offerPriceLabel(o: MembershipOffer | undefined): string {
  if (!o || o.currentPricePence === null) return '';
  const p = o.currentPricePence / 100;
  const amount = Number.isInteger(p) ? `£${p}` : `£${p.toFixed(2)}`;
  return `${amount}${o.billing ? ` ${o.billing}` : ''}`;
}

export interface MembershipOffersState {
  loading: boolean;
  offers: MembershipOffer[];
  error: string | null;
}

let cachedOffers: MembershipOffer[] | null = null;
let offersInflight: Promise<MembershipOffer[]> | null = null;

function fetchOffersOnce(): Promise<MembershipOffer[]> {
  if (cachedOffers) return Promise.resolve(cachedOffers);
  if (!offersInflight) {
    offersInflight = fetchMembershipOffers()
      .then((o) => {
        cachedOffers = o;
        return o;
      })
      .finally(() => {
        offersInflight = null;
      });
  }
  return offersInflight;
}

export function useMembershipOffers(): MembershipOffersState {
  const [state, setState] = useState<MembershipOffersState>(
    cachedOffers ? { loading: false, offers: cachedOffers, error: null } : { loading: true, offers: [], error: null }
  );

  useEffect(() => {
    if (cachedOffers) return;
    let cancelled = false;
    fetchOffersOnce()
      .then((offers) => {
        if (!cancelled) setState({ loading: false, offers, error: null });
      })
      .catch((e: any) => {
        if (!cancelled) setState({ loading: false, offers: [], error: e?.message || 'Could not load memberships' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
